// The detail cards, as data.
//
// A card is an entry in one array, and adding "publishing hours", "approval
// turnaround" or "per-person activity" later means appending here and adding a
// key to `workspace_dashboard()`. Nothing lays them out by name: the screen
// renders whatever survives the permission and `when` filters, so a workspace
// with no failures is not shown an empty Failures card.
//
// What is NOT here: the four numbers at the top of the screen, the composer,
// the preview, the upcoming schedule, the performance chart, the recent posts
// and the drafts. Those have fixed places in the dashboard's layout and
// components of their own — a registry is the right shape for "however many of
// these there turn out to be", and the wrong shape for "this one, here".

import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { BarList, Meter } from "./charts";
import { PLATFORM_LABEL, type Platform } from "../lib/types";
import { countdown, health, nf, rate, Stat, when } from "./format";
import type { Dashboard } from "./types";

export interface DashboardCard {
  id: string;
  title: string;
  /** One line under the title, in the reader's terms rather than the table's. */
  lede?: (d: Dashboard) => string;
  /** A permission this card needs beyond `post.view`, which the page requires. */
  permission?: string;
  /** False when there is nothing worth a card. Absent cards beat empty ones. */
  when?: (d: Dashboard) => boolean;
  /** Grid span. "full" is the width of the page. */
  size?: "full" | "half";
  /**
   * `zone` is the reader's chosen one, handed down rather than read here: these
   * are plain functions, not components, so they cannot call a hook of their
   * own — and a card that reached for the browser's zone would disagree with
   * the calendar two clicks away.
   */
  render: (d: Dashboard, zone: string) => ReactNode;
}

export const CARDS: DashboardCard[] = [
  {
    id: "platforms",
    title: "Where it went",
    size: "half",
    lede: () => "Deliveries published per network in this window.",
    when: (d) => d.platforms.some((p) => p.published + p.failed + p.connections > 0),
    render: (d) => (
      <BarList
        valueLabel="deliveries published"
        rows={d.platforms.map((p) => ({
          label: PLATFORM_LABEL[p.platform as Platform] ?? p.platform,
          value: p.published,
          note: p.connections === 0
            ? "no account connected"
            : p.failed > 0
              // The rate, not just the count: "2 failed" reads differently
              // against 3 attempts than against 300.
              ? `${nf.format(p.failed)} failed — ${rate(p.published, p.published + p.failed)} got through`
              : null,
        }))}
      />
    ),
  },

  {
    id: "destinations",
    title: "The accounts you publish to",
    size: "full",
    // Seeing the accounts is its own permission (ADR-0011), and the server
    // scopes the rows besides: an agency lent one Page gets one row back.
    permission: "connection.view",
    when: (d) => d.destinations.length > 0,
    lede: (d) => {
      const n = d.destinations.length;
      const bad = d.destinations.filter((x) => health(x).tone !== "ok").length;
      return bad === 0
        ? `${n} account${n === 1 ? "" : "s"} connected, all of them working.`
        : `${n} account${n === 1 ? "" : "s"} connected — ${bad} need${bad === 1 ? "s" : ""} attention.`;
    },
    render: (d, zone) => (
      <ul className="dash-destinations">
        {d.destinations.map((x) => {
          const h = health(x);
          return (
            <li key={x.id}>
              {/* The picture the network gave us, not an initial in a circle:
                  someone with four Pages recognises the avatar before the name. */}
              {x.avatar_url
                ? <img className="avatar" src={x.avatar_url} alt="" loading="lazy" />
                : <div className="avatar" />}

              <div className="dest-who">
                <strong>{x.display_name}</strong>
                <span className="muted">
                  {PLATFORM_LABEL[x.platform as Platform] ?? x.platform}
                  {/* Instagram publishes with the linked Page's token, so which
                      Page it arrived through is the answer to "why did this stop
                      working when I unlinked that Page". */}
                  {x.linked_page_name ? ` · via ${x.linked_page_name}` : ""}
                  {" · "}
                  <code>{x.provider_account_id}</code>
                </span>
              </div>

              {/* Ours, and labelled as ours. Nothing on this row came back from
                  the network after the account was connected. */}
              <div className="dest-counts">
                <span><strong>{nf.format(x.published)}</strong> published</span>
                {x.failed > 0 && (
                  <span className="err"><strong>{nf.format(x.failed)}</strong> failed</span>
                )}
                <span className="muted">
                  {x.last_published_at
                    ? `last post ${when(x.last_published_at, zone)}`
                    : "nothing sent yet"}
                </span>
              </div>

              <span className={`chip ${h.tone}`}>{h.label}</span>
            </li>
          );
        })}
      </ul>
    ),
  },

  {
    id: "reliability",
    title: "Did it actually go out?",
    size: "half",
    // Only once there is something to be reliable about. A success rate over
    // three deliveries is noise dressed as a measurement.
    when: (d) => d.totals.deliveries_published + d.totals.deliveries_failed >= 5,
    lede: (d) => `Across ${nf.format(d.totals.deliveries_published + d.totals.deliveries_failed)} attempts in this window.`,
    render: (d) => {
      const t = d.totals;
      const attempted = t.deliveries_published + t.deliveries_failed;
      const worst = [...d.platforms]
        .filter((p) => p.published + p.failed > 0)
        .sort((a, b) => (a.published / (a.published + a.failed)) - (b.published / (b.published + b.failed)))[0];
      const clean = t.deliveries_failed === 0;

      return (
        <>
          <div className="stat-row">
            <Stat label="Got through" value={t.deliveries_published}
                  tone={clean ? "ok" : undefined}
                  hint={`${rate(t.deliveries_published, attempted)} of everything attempted`} />
          </div>
          <Meter value={t.deliveries_published} of={attempted} tone={clean ? "ok" : "warn"} />
          <p className="muted">
            {clean
              ? "Every delivery in this window succeeded."
              : <>
                  {nf.format(t.deliveries_failed)} did not.
                  {worst && worst.failed > 0 && <>
                    {" "}The weakest link is{" "}
                    <strong>{PLATFORM_LABEL[worst.platform as Platform] ?? worst.platform}</strong>
                    {" "}at {rate(worst.published, worst.published + worst.failed)}.
                  </>}
                  {" "}<Link to="/posts">See which posts</Link>.
                </>}
          </p>
        </>
      );
    },
  },

  {
    id: "attention",
    title: "Needs a look",
    size: "half",
    lede: () => "Everything here is something to do, not something to read.",
    when: (d) =>
      d.failures.length > 0 ||
      d.connections.expired + d.connections.revoked
        + d.connections.expiring_soon + d.connections.lapsed > 0,
    render: (d) => (
      <ul className="dash-attention">
        {/* Grouped by why, because forty of the same failure is one problem. */}
        {d.failures.map((f) => (
          <li key={`${f.platform}-${f.error_code}`}>
            <span className="chip err">{nf.format(f.count)}</span>
            <span>
              <strong>{PLATFORM_LABEL[f.platform as Platform] ?? f.platform}</strong>
              {" — "}
              {f.example ?? f.error_code}
            </span>
          </li>
        ))}
        {d.connections.lapsed > 0 && (
          <li>
            <span className="chip err">{d.connections.lapsed}</span>
            <span>
              {/* The connection list says "active" because that flag is only
                  set when a publish fails, and nothing has tried yet. Counted
                  only where the credential cannot renew itself — a YouTube
                  token past its hour is routine, a Meta one is dead. */}
              account{d.connections.lapsed === 1 ? "" : "s"} still shown as connected, but
              {d.connections.lapsed === 1 ? " its" : " their"} access has expired and cannot
              renew itself. The next post to {d.connections.lapsed === 1 ? "it" : "them"} will
              fail — <Link to="/connections">reconnect now</Link>.
            </span>
          </li>
        )}
        {d.connections.expiring_soon > 0 && (
          <li>
            <span className="chip warn">{d.connections.expiring_soon}</span>
            <span>
              {/* The failure mode nobody sees coming, and the one no competitor
                  puts on a dashboard: the token ages out and the first sign is
                  a post that did not publish. A count is a fact; a countdown is
                  something to act on. */}
              account{d.connections.expiring_soon === 1 ? "'s" : "s'"} access expires
              {d.connections.expiring_soonest
                ? <> {countdown(d.connections.expiring_soonest)}</>
                : <> within a fortnight</>}
              {" "}— <Link to="/connections">reconnect</Link> before it does, or the next post to
              them simply fails.
            </span>
          </li>
        )}
        {d.connections.expired > 0 && (
          <li>
            <span className="chip err">{d.connections.expired}</span>
            <span>expired — <Link to="/connections">reconnect</Link> to publish to them again.</span>
          </li>
        )}
        {d.connections.revoked > 0 && (
          <li>
            <span className="chip">{d.connections.revoked}</span>
            <span>revoked at the network. Nothing can go out to them.</span>
          </li>
        )}
      </ul>
    ),
  },

  {
    id: "library",
    title: "The library",
    size: "half",
    permission: "media.view",
    when: (d) => d.library.assets > 0,
    lede: () => "Storage you are paying for, and what has never been used.",
    render: (d) => {
      const used = d.library.assets - d.library.never_published;
      return (
        <>
          <div className="stat-row">
            <Stat label="Files stored" value={d.library.assets} />
            <Stat label="Never published" value={d.library.never_published}
                  tone={d.library.never_published > 0 ? "warn" : undefined}
                  hint={<Link to="/media">open the library</Link>} />
          </div>
          <Meter value={used} of={d.library.assets} tone="ok" />
          <p className="muted">
            {nf.format(used)} of {nf.format(d.library.assets)} file
            {d.library.assets === 1 ? " has" : "s have"} been published at least once.
          </p>
        </>
      );
    },
  },
];
